// Shared canvas drawing for Sprite2D, used by both 2D scenes
// (Platformer2DScene.tsx and Tilemap2DScene.tsx). Previously duplicated
// verbatim in both files; extracted here so there is one implementation to
// change, not two that can silently drift apart.
import * as THREE from 'three';

import type { Sprite2D } from '../../content/schema/map';

const cellsCanvasCache = new WeakMap<(string | null)[], HTMLCanvasElement>();

/** True in a real browser, false under Vitest's default Node environment
 * (no jsdom/DOM anywhere in this repo's test setup - see this file's own
 * `drawSpriteCells`/`drawSprite`, already DOM-only and untested directly
 * for the same reason). Checked once per call rather than cached, it's a
 * cheap `typeof` check and the answer cannot change mid-process. */
function hasRealDom(): boolean {
  return typeof document !== 'undefined';
}

/**
 * Renders `cells` to a small offscreen canvas once, keyed by the array's
 * own identity, and reuses it on every later call. Relies on a cells array
 * never being mutated in place once built - true everywhere in this
 * codebase today (confirmed by grep across both this repo and its game
 * consumers before adding this cache): a new animation frame or a new bake
 * is always a new array, the same stability assumption `tilemapPaintRegion.
 * ts`'s own dirty-rect diffing already relies on for a tile id's
 * appearance. Memory-safe (a `WeakMap`, so an unreferenced cells array's
 * cached canvas is collected with it). Only called when {@link hasRealDom}
 * is true.
 */
function cachedCellsCanvas(size: number, cells: (string | null)[]): HTMLCanvasElement {
  const cached = cellsCanvasCache.get(cells);
  if (cached) return cached;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  for (let i = 0; i < cells.length; i++) {
    const colour = cells[i];
    if (!colour) continue;
    ctx.fillStyle = colour;
    ctx.fillRect(i % size, Math.floor(i / size), 1, 1);
  }
  cellsCanvasCache.set(cells, canvas);
  return canvas;
}

/** The direct per-pixel fallback `paintSpriteCellsAt` used before the
 * cached-canvas optimisation, kept as the path taken with no real DOM
 * (Vitest) - the fixture-sized grids tests use make the perf difference
 * irrelevant there, and this keeps the function testable with a plain
 * recording-context stub, no jsdom dependency added to this repo. */
function paintSpriteCellsAtDirect(
  ctx: CanvasRenderingContext2D,
  size: number,
  cells: (string | null)[],
  xPx: number,
  yPx: number,
  scale: number,
): void {
  for (let i = 0; i < cells.length; i++) {
    const colour = cells[i];
    if (!colour) continue;
    const column = i % size;
    const row = Math.floor(i / size);
    ctx.fillStyle = colour;
    ctx.fillRect(xPx + column * scale, yPx + row * scale, scale, scale);
  }
}

/**
 * Paint `cells` (a row-major `size*size` colour grid, null meaning
 * transparent) into an already-sized canvas context at pixel offset
 * (`xPx`, `yPx`), scaled to `size * scale` device pixels square. A
 * generalisation of {@link drawSpriteCells}: where that function always
 * paints a fresh canvas at the origin one texel per cell, this paints into
 * an arbitrary existing context at an arbitrary offset and scale - what
 * painting one tile's baked art within a larger shared tilemap canvas
 * needs.
 *
 * In a real browser, internally blits a cached offscreen render of `cells`
 * ({@link cachedCellsCanvas}) rather than re-walking every pixel on every
 * call. This matters specifically for tile art, where the same small
 * sprite is painted at many positions across one map: measured at roughly
 * 100 to 250 times cheaper than a naive per-pixel loop for a repeated
 * 16x16 tile (see `sstk-furrow`'s `docs/production/rendering-at-scale.
 * md`), and produces pixel-identical output (a transparent source pixel
 * leaves the destination untouched under canvas 2D's default compositing,
 * the same as the old per-pixel loop skipping null cells outright). Falls
 * back to the direct per-pixel loop with no real DOM (see {@link
 * paintSpriteCellsAtDirect}), which is what this repo's Vitest suite
 * exercises.
 */
export function paintSpriteCellsAt(
  ctx: CanvasRenderingContext2D,
  size: number,
  cells: (string | null)[],
  xPx: number,
  yPx: number,
  scale = 1,
): void {
  if (!hasRealDom()) {
    paintSpriteCellsAtDirect(ctx, size, cells, xPx, yPx, scale);
    return;
  }
  const cached = cachedCellsCanvas(size, cells);
  const prevSmoothing = ctx.imageSmoothingEnabled;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(cached, xPx, yPx, size * scale, size * scale);
  ctx.imageSmoothingEnabled = prevSmoothing;
}

export function drawSpriteCells(canvas: HTMLCanvasElement, size: number, cells: (string | null)[]): void {
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, size, size);
  paintSpriteCellsAt(ctx, size, cells, 0, 0, 1);
}

export function drawSprite(sprite: Sprite2D): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = sprite.size;
  canvas.height = sprite.size;
  drawSpriteCells(canvas, sprite.size, sprite.cells);
  return canvas;
}

export function canvasTexture(canvas: HTMLCanvasElement): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.colorSpace = THREE.SRGBColorSpace;
  // The canvas 2D API produces straight-alpha data (RGB independent of
  // alpha). Three.js CanvasTexture defaults to premultiplyAlpha=true,
  // which tells WebGL to treat the source as already premultiplied and
  // blend it directly. That gives incorrect results with straight-alpha
  // data: transparent regions around drawn pixels render as solid black
  // because WebGL skips the premultiplication pass it would otherwise
  // apply. Setting this to false tells Three.js to premultiply itself
  // before upload, which is the correct path for canvas-drawn sprites.
  texture.premultiplyAlpha = false;
  return texture;
}
